import type { AvatarConfig } from '@runway/shared';
import { cx } from '../../ui/cx';

export type UserAvatarProps = {
  displayName: string;
  picture?: string | null;
  /** Colours the placeholder from the player's avatar (hair ring, skin fill). */
  avatar?: AvatarConfig | null;
  size?: 'sm' | 'md' | 'lg';
  /** Show a presence dot. */
  online?: boolean | null;
  className?: string;
};

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '?';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + second).toUpperCase();
}

/** Round avatar thumbnail: photo if present, else initials tinted by the player's avatar colours. */
export function UserAvatar({ displayName, picture, avatar, size = 'md', online = null, className }: UserAvatarProps) {
  const style = avatar
    ? ({
        '--avatar-ring': avatar.hair.color,
        background: `linear-gradient(135deg, ${avatar.skin}, ${mix(avatar.skin)})`,
        color: readableOn(avatar.skin),
      } as React.CSSProperties)
    : undefined;
  const dot = (
    <span
      className={cx('avatar-dot', size === 'lg' && 'avatar-dot-lg', size === 'sm' && 'avatar-dot-sm', className)}
      style={style}
      aria-hidden="true"
      title={displayName}
    >
      {picture ? <img src={picture} alt="" referrerPolicy="no-referrer" /> : initialsOf(displayName)}
    </span>
  );
  if (online === null) return dot;
  return <span className={cx('presence', online && 'presence-online')}>{dot}</span>;
}

/** Slightly darker/shifted companion colour for a soft gradient. */
function mix(hex: string): string {
  const { r, g, b } = parse(hex);
  const f = (v: number) => Math.max(0, Math.min(255, Math.round(v * 0.82)));
  return `rgb(${f(r)}, ${f(g + 8)}, ${f(b + 24)})`;
}

function readableOn(hex: string): string {
  const { r, g, b } = parse(hex);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#1a0f2e' : '#fff7fb';
}

function parse(hex: string): { r: number; g: number; b: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m || !m[1]) return { r: 255, g: 62, b: 157 };
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
